/**
 * IntegrationsTab Component
 *
 * Displays calendar sync management UI for guides.
 * All three providers (Google Calendar, Apple Calendar, Outlook) share
 * a single iCal feed token — the same subscribe URL appears in every section.
 *
 * MVP 1.12.4 C-11
 * Story: SCRUM-1170
 * Location: src/components/account/IntegrationsTab.jsx
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link as _Link } from 'lucide-react';
import apiRequest from '../../services/api';
import ConfirmModal from '../shared/ConfirmModal';

/** Confirmation dialog text — spec-mandated exact strings */
const REGENERATE_WARNING =
  'Regenerating will disconnect any calendars currently subscribed. They will stop receiving updates until they re-subscribe with the new link.';

const DISCONNECT_WARNING = 'This will disconnect all subscribed calendars immediately.';

/**
 * StatusBadge — renders Connected (green) or Not connected (grey)
 */
function StatusBadge({ active }) {
  if (active) {
    return (
      <span
        className="text-white text-xs font-medium px-2 py-0.5 rounded-full"
        style={{ backgroundColor: '#009263' }}
        data-testid="status-badge-connected"
      >
        Connected
      </span>
    );
  }
  return (
    <span
      className="bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full"
      data-testid="status-badge-disconnected"
    >
      Not connected
    </span>
  );
}

/**
 * SubscribeUrlRow — shows the feed URL with a copy button (only when active)
 */
function SubscribeUrlRow({ subscribeUrl, onCopy, copyFeedback, testIdPrefix }) {
  if (!subscribeUrl) { return null; }
  return (
    <div className="flex gap-2 mt-2 mb-3">
      <input
        type="text"
        readOnly
        value={subscribeUrl}
        className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 truncate"
        data-testid={`${testIdPrefix}-subscribe-url`}
        aria-label="Subscribe URL"
      />
      <button
        type="button"
        onClick={onCopy}
        className="px-3 py-2 text-xs font-medium text-white rounded-lg whitespace-nowrap transition-colors"
        style={{ backgroundColor: '#009263' }}
        data-testid={`${testIdPrefix}-copy-button`}
      >
        {copyFeedback ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * ProviderSection — renders one provider's card with badge, URL, and actions
 */
function ProviderSection({ title, subscribeUrl, hasActiveToken, copyFeedback, onCopy, onRegenerate, onDisconnect, testIdPrefix, children }) {
  return (
    <div
      className="border border-gray-200 rounded-lg p-4"
      data-testid={`${testIdPrefix}-section`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <StatusBadge active={hasActiveToken} />
      </div>

      <SubscribeUrlRow
        subscribeUrl={subscribeUrl}
        onCopy={onCopy}
        copyFeedback={copyFeedback}
        testIdPrefix={testIdPrefix}
      />

      {children}

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={onRegenerate}
          className="text-xs font-medium text-gray-600 hover:text-gray-900 underline transition-colors"
          data-testid={`${testIdPrefix}-regenerate-button`}
        >
          Regenerate link
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          className="text-xs font-medium text-red-600 hover:text-red-800 underline transition-colors"
          data-testid={`${testIdPrefix}-disconnect-button`}
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

/**
 * IntegrationsTab
 *
 * Main component — fetches token status on mount and renders three provider sections.
 * All three sections display the same subscribe URL (one token for all providers).
 */
export default function IntegrationsTab() {
  const [status, setStatus] = useState({
    hasActiveToken: false,
    subscribeUrl: null,
    loaded: false,
  });
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch token status on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const result = await apiRequest('/api/v1/calendar/feed/token/status');
        if (!cancelled) {
          setStatus({
            hasActiveToken: result.data.hasActiveToken,
            subscribeUrl: result.data.subscribeUrl,
            loaded: true,
          });
        }
      } catch {
        if (!cancelled) {
          setStatus(prev => ({ ...prev, loaded: true }));
        }
      }
    }

    fetchStatus();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!status.subscribeUrl) { return; }
    try {
      await navigator.clipboard.writeText(status.subscribeUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      // Clipboard unavailable — no-op
    }
  }, [status.subscribeUrl]);

  const openRegenerate = useCallback(() => {
    setConfirmDialog({ open: true, action: 'regenerate' });
  }, []);

  const openDisconnect = useCallback(() => {
    setConfirmDialog({ open: true, action: 'disconnect' });
  }, []);

  const closeDialog = useCallback(() => {
    setConfirmDialog({ open: false, action: null });
  }, []);

  const handleConfirm = useCallback(async () => {
    const { action } = confirmDialog;
    setActionLoading(true);
    try {
      if (action === 'regenerate') {
        const result = await apiRequest('/api/v1/calendar/feed/token', { method: 'POST' });
        setStatus({
          hasActiveToken: true,
          subscribeUrl: result.data.subscribeUrl,
          loaded: true,
        });
      } else if (action === 'disconnect') {
        await apiRequest('/api/v1/calendar/feed/token', { method: 'DELETE' });
        setStatus({ hasActiveToken: false, subscribeUrl: null, loaded: true });
      }
    } catch {
      // Errors bubble to global handler via apiRequest
    } finally {
      setActionLoading(false);
    }
  }, [confirmDialog]);

  const isRegenerate = confirmDialog.action === 'regenerate';

  return (
    <div data-testid="integrations-tab">
      {/* Info box — always visible */}
      <div
        className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
        data-testid="info-box"
      >
        <p className="text-sm text-blue-800">
          <strong>One link works for all calendar apps.</strong> Add your schedule to Google Calendar, Apple Calendar, Outlook, or any app that supports iCal subscriptions using the same subscribe URL.
        </p>
      </div>

      <div className="space-y-4">
        {/* Google Calendar */}
        <ProviderSection
          title="Google Calendar"
          subscribeUrl={status.subscribeUrl}
          hasActiveToken={status.hasActiveToken}
          copyFeedback={copyFeedback}
          onCopy={handleCopy}
          onRegenerate={openRegenerate}
          onDisconnect={openDisconnect}
          testIdPrefix="google"
        />

        {/* Apple Calendar / iCal */}
        <ProviderSection
          title="Apple Calendar / iCal"
          subscribeUrl={status.subscribeUrl}
          hasActiveToken={status.hasActiveToken}
          copyFeedback={copyFeedback}
          onCopy={handleCopy}
          onRegenerate={openRegenerate}
          onDisconnect={openDisconnect}
          testIdPrefix="apple"
        />

        {/* Outlook */}
        <ProviderSection
          title="Outlook"
          subscribeUrl={status.subscribeUrl}
          hasActiveToken={status.hasActiveToken}
          copyFeedback={copyFeedback}
          onCopy={handleCopy}
          onRegenerate={openRegenerate}
          onDisconnect={openDisconnect}
          testIdPrefix="outlook"
        >
          {/* Outlook-specific step-by-step setup instructions */}
          <div className="mt-3 text-xs text-gray-600 space-y-1" data-testid="outlook-instructions">
            <p className="font-medium text-gray-800">Outlook setup instructions:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Open Outlook</li>
              <li>File &rarr; Account Settings &rarr; Account Settings</li>
              <li>Internet Calendars tab &rarr; New</li>
              <li>Paste the subscribe URL &rarr; Add</li>
              <li>Save &amp; Close</li>
            </ol>
          </div>
        </ProviderSection>
      </div>

      {/* Confirmation dialogs */}
      <ConfirmModal
        isOpen={confirmDialog.open && isRegenerate}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title="Regenerate calendar link?"
        message={REGENERATE_WARNING}
        confirmText={actionLoading ? 'Regenerating…' : 'Regenerate'}
        cancelText="Cancel"
      />

      <ConfirmModal
        isOpen={confirmDialog.open && !isRegenerate}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title="Disconnect all calendars?"
        message={DISCONNECT_WARNING}
        confirmText={actionLoading ? 'Disconnecting…' : 'Disconnect'}
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}
